import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { parseIDR } from "@/lib/format";
import { Button, Field, Input, Select, Modal, Badge } from "@/components/ui-kit";
import { Plus, Trash2, MapPin, EyeOff, Eye } from "lucide-react";

/**
 * Iter v34aw · Batch 2 rev — GroupedMaterialEditor (shared prices model)
 *
 * SHARED at material level:  price_non_vat, price_ex_vat, price_with_vat, unloading_fee, category
 * PER-LOCATION only:         delivery_location, delivery_cost, catalog_hidden, catalog_visible_fields
 *
 * On save, shared values are applied to ALL variant rows (backend still stores
 * one row per location — we sync prices across them).
 */
export function GroupedMaterialEditor({ materialName, unit, onClose, onSaved, categories = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState(materialName || "");
  const [newUnit, setNewUnit] = useState(unit || "");

  // Shared material-level fields
  const [shared, setShared] = useState({
    price_non_vat: 0,
    price_ex_vat: 0,
    price_with_vat: 0,
    unloading_fee: 0,
    category: "delivery",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/prices");
        const nm = (materialName || "").trim().toLowerCase();
        const un = (unit || "").trim().toLowerCase();
        const filtered = (r.data || []).filter(
          (p) => (p.material_name || "").trim().toLowerCase() === nm && (p.unit || "").trim().toLowerCase() === un,
        );
        // Take shared values from first row
        if (filtered.length > 0) {
          const first = filtered[0];
          setShared({
            price_non_vat: first.price_non_vat || 0,
            price_ex_vat: first.price_ex_vat || 0,
            price_with_vat: first.price_with_vat || 0,
            unloading_fee: first.unloading_fee || 0,
            category: first.category || "delivery",
          });
        }
        setRows(filtered.map((p) => ({ ...p, _dirty: false })));
      } catch (e) {
        toast.error("Gagal memuat data harga.");
      } finally {
        setLoading(false);
      }
    })();
  }, [materialName, unit]);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch, _dirty: true } : r)));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: null,
        _new: true,
        _dirty: true,
        delivery_location: "",
        delivery_cost: 0,
        catalog_hidden: false,
        catalog_visible_fields: [],
      },
    ]);
  };

  const removeRow = (idx) => {
    if (!window.confirm("Hapus lokasi ini?")) return;
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, _deleted: true } : r)));
  };

  const undoDelete = (idx) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, _deleted: false } : r)));

  const updateShared = (patch) => {
    setShared((prev) => ({ ...prev, ...patch }));
    // Mark all rows dirty since shared changes propagate to all
    setRows((prev) => prev.map((r) => (r._deleted ? r : { ...r, _dirty: true })));
  };

  // Iter v34cu (#2) — Ganti NAMA MATERIAL / SATUAN juga harus menandai semua baris
  // lokasi sebagai dirty. Sebelumnya tidak, sehingga perubahan nama tidak pernah
  // tersimpan (nama kembali seperti semula setelah modal ditutup).
  const markAllDirty = () => setRows((prev) => prev.map((r) => (r._deleted ? r : { ...r, _dirty: true })));
  const changeMaterialName = (v) => { setNewMaterialName(v); markAllDirty(); };
  const changeUnit = (v) => { setNewUnit(v); markAllDirty(); };

  const onSharedExVatChange = (v) => {
    const num = parseIDR(v);
    const derived = num > 0 ? Math.round(num * 1.11 * 100) / 100 : 0;
    updateShared({ price_ex_vat: v, price_with_vat: derived });
  };
  const onSharedWithVatChange = (v) => {
    const num = parseIDR(v);
    const derived = num > 0 ? Math.round((num / 1.11) * 100) / 100 : 0;
    updateShared({ price_with_vat: v, price_ex_vat: derived });
  };

  const buildPayload = (r) => {
    let priceWithVat = parseIDR(shared.price_with_vat);
    let priceExVat = parseIDR(shared.price_ex_vat);
    if (priceWithVat > 0 && priceExVat === 0) priceExVat = Math.round((priceWithVat / 1.11) * 100) / 100;
    else if (priceExVat > 0 && priceWithVat === 0) priceWithVat = Math.round(priceExVat * 1.11 * 100) / 100;
    return {
      delivery_location: r.delivery_location || "",
      material_name: newMaterialName,
      unit: newUnit,
      client_name: "",
      // SHARED values applied to every row
      price_non_vat: Math.round(parseIDR(shared.price_non_vat)),
      price_ex_vat: priceExVat,
      price_with_vat: priceWithVat,
      unloading_fee: Math.round(parseIDR(shared.unloading_fee)),
      category: shared.category || "delivery",
      // PER-LOCATION values
      delivery_cost: Math.round(parseIDR(r.delivery_cost)),
      catalog_hidden: !!r.catalog_hidden,
      catalog_visible_fields: Array.isArray(r.catalog_visible_fields) ? r.catalog_visible_fields : [],
    };
  };

  const saveAll = async () => {
    if (!newMaterialName.trim() || !newUnit.trim()) {
      toast.error("Nama material dan satuan wajib diisi.");
      return;
    }
    const kept = rows.filter((r) => !r._deleted);
    if (kept.length === 0) {
      toast.error("Minimal 1 lokasi harus tersimpan (atau tekan Batal).");
      return;
    }
    setSaving(true);
    let okCount = 0;
    let failCount = 0;
    try {
      for (const r of rows) {
        try {
          if (r._deleted && r.id) {
            await api.delete(`/prices/${r.id}`);
            okCount++;
          } else if (r._new && !r._deleted) {
            await api.post("/prices", buildPayload(r));
            okCount++;
          } else if (r._dirty && !r._deleted && r.id) {
            await api.patch(`/prices/${r.id}`, buildPayload(r));
            okCount++;
          }
        } catch (e) {
          failCount++;
          console.error("Save row failed", e?.response?.data);
        }
      }
      if (failCount === 0) toast.success(`Tersimpan (${okCount} perubahan).`);
      else toast.warning(`${okCount} tersimpan, ${failCount} gagal.`);
      onSaved?.();
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const visibleRows = rows.filter((r) => !r._deleted);

  return (
    <Modal open onClose={onClose} title={`Edit Material: ${materialName || "Baru"}`} size="xl">
      <div className="space-y-5" data-testid="grouped-material-editor">
        {/* Identity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-blue-50/60 border border-blue-100 p-4 rounded-xl">
          <Field label="Nama Material" required>
            <Input
              value={newMaterialName}
              onChange={(e) => changeMaterialName(e.target.value)}
              data-testid="grp-material-name"
              placeholder="Pasir Cor"
            />
          </Field>
          <Field label="Satuan" required>
            <Input
              value={newUnit}
              onChange={(e) => changeUnit(e.target.value)}
              data-testid="grp-material-unit"
              placeholder="Kubik / Ton / Sak"
            />
          </Field>
          <Field label="Kategori">
            <Select
              value={shared.category}
              onChange={(e) => updateShared({ category: e.target.value })}
              data-testid="grp-material-category"
            >
              {categories.length === 0 ? (
                <>
                  <option value="delivery">delivery</option>
                  <option value="service">service</option>
                </>
              ) : categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
        </div>

        {/* Shared Prices */}
        <div className="rounded-xl border border-stone-200 p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Harga & Biaya Bongkar</p>
              <p className="text-[11px] text-stone-500 mt-0.5">Nilai berikut berlaku untuk <b>semua lokasi</b> material ini.</p>
            </div>
            <Badge tone="stone">Sinkron ke {visibleRows.length} lokasi</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Harga Non PPN">
              <Input
                value={shared.price_non_vat}
                onChange={(e) => updateShared({ price_non_vat: e.target.value })}
                className="font-mono-num text-right"
                data-testid="grp-shared-non-vat"
                placeholder="0"
              />
            </Field>
            <Field label="Harga Exc PPN">
              <Input
                value={shared.price_ex_vat}
                onChange={(e) => onSharedExVatChange(e.target.value)}
                className="font-mono-num text-right"
                data-testid="grp-shared-ex-vat"
                placeholder="0"
              />
            </Field>
            <Field label="Harga + PPN 11%">
              <Input
                value={shared.price_with_vat}
                onChange={(e) => onSharedWithVatChange(e.target.value)}
                className="font-mono-num text-right"
                data-testid="grp-shared-with-vat"
                placeholder="0"
              />
            </Field>
            <Field label="Biaya Bongkar">
              <Input
                value={shared.unloading_fee}
                onChange={(e) => updateShared({ unloading_fee: e.target.value })}
                className="font-mono-num text-right"
                data-testid="grp-shared-unloading"
                placeholder="0"
              />
            </Field>
          </div>
          <p className="text-[11px] text-stone-500 mt-3 bg-stone-50 border border-stone-200 px-3 py-2 rounded">
            <strong>Auto-sync PPN:</strong> Edit &quot;Harga Exc PPN&quot; → &quot;+ PPN&quot; otomatis × 1,11 · Edit &quot;+ PPN&quot; → &quot;Exc PPN&quot; otomatis ÷ 1,11.
          </p>
        </div>

        {/* Per-location Ongkos Kirim */}
        <div className="rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-600 font-semibold">Ongkos Kirim per Lokasi</p>
              <p className="text-[11px] text-stone-500 mt-0.5">Tambahkan setiap kota/lokasi dengan ongkir masing-masing. Bisa lebih dari 1.</p>
            </div>
            <Button variant="secondary" onClick={addRow} data-testid="grp-add-location-btn">
              <Plus className="h-4 w-4" /> Tambah Lokasi
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-6 text-stone-500 text-sm">Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-[10px] uppercase tracking-wider text-stone-600 font-semibold">
                    <th className="px-3 py-2 w-48">Lokasi / Kota</th>
                    <th className="px-3 py-2 text-right w-40">Ongkos Kirim</th>
                    <th className="px-3 py-2 w-28 text-center">Katalog Klien</th>
                    <th className="px-3 py-2">Visibility Field (Katalog)</th>
                    <th className="px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-stone-400">Belum ada lokasi. Klik &quot;Tambah Lokasi&quot;.</td></tr>
                  )}
                  {rows.map((r, idx) => {
                    if (r._deleted) {
                      return (
                        <tr key={idx} className="bg-red-50/60 text-red-800 border-b border-stone-100">
                          <td colSpan={4} className="px-3 py-2 italic">
                            <MapPin className="inline h-3 w-3 mr-1" />{r.delivery_location || "(kosong)"} — <b>akan dihapus</b>
                          </td>
                          <td className="px-2 py-2 text-right">
                            <button onClick={() => undoDelete(idx)} className="text-[10px] underline">Batal</button>
                          </td>
                        </tr>
                      );
                    }
                    const visFields = Array.isArray(r.catalog_visible_fields) ? r.catalog_visible_fields : [];
                    const allVisible = visFields.length === 0;
                    const isFieldVisible = (fname) => allVisible || visFields.includes(fname);
                    const toggleField = (fname) => {
                      let next;
                      if (allVisible) {
                        const ALL = ["price_non_vat", "price_ex_vat", "price_with_vat", "delivery_cost", "unloading_fee"];
                        next = ALL.filter((f) => f !== fname);
                      } else if (visFields.includes(fname)) {
                        next = visFields.filter((f) => f !== fname);
                      } else {
                        next = [...visFields, fname];
                      }
                      updateRow(idx, { catalog_visible_fields: next });
                    };
                    return (
                      <tr key={idx} className={`border-b border-stone-100 ${r._new ? "bg-emerald-50/40" : r._dirty ? "bg-amber-50/40" : ""}`} data-testid={`grp-row-${idx}`}>
                        <td className="px-3 py-2">
                          <Input
                            value={r.delivery_location || ""}
                            onChange={(e) => updateRow(idx, { delivery_location: e.target.value })}
                            className="!h-8 !text-xs"
                            placeholder="Jakarta / Palembang / ..."
                            data-testid={`grp-loc-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={r.delivery_cost || ""}
                            onChange={(e) => updateRow(idx, { delivery_cost: e.target.value })}
                            className="!h-8 !text-xs font-mono-num text-right"
                            placeholder="0"
                            data-testid={`grp-ongkir-${idx}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <label className="inline-flex items-center gap-1 cursor-pointer" title={r.catalog_hidden ? "Sembunyi dari katalog klien" : "Tampil di katalog klien"}>
                            <input
                              type="checkbox"
                              checked={!r.catalog_hidden}
                              onChange={(e) => updateRow(idx, { catalog_hidden: !e.target.checked })}
                              data-testid={`grp-catalog-visible-${idx}`}
                            />
                            {r.catalog_hidden ? <EyeOff className="h-3.5 w-3.5 text-stone-400" /> : <Eye className="h-3.5 w-3.5 text-emerald-600" />}
                          </label>
                        </td>
                        <td className="px-3 py-2">
                          {r.catalog_hidden ? (
                            <span className="text-[10px] text-stone-400">—</span>
                          ) : (
                            <details className="text-[10px]">
                              <summary className="cursor-pointer text-stone-500 hover:text-stone-700 select-none">
                                {allVisible ? "Semua field" : `${visFields.length} field`}
                              </summary>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {[
                                  ["price_non_vat", "Non PPN"],
                                  ["price_ex_vat", "Exc PPN"],
                                  ["price_with_vat", "+ PPN"],
                                  ["delivery_cost", "Ongkir"],
                                  ["unloading_fee", "Bongkar"],
                                ].map(([fname, flabel]) => (
                                  <label key={fname} className="inline-flex items-center gap-1 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isFieldVisible(fname)}
                                      onChange={() => toggleField(fname)}
                                      data-testid={`grp-field-${fname}-${idx}`}
                                    />
                                    {flabel}
                                  </label>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <button onClick={() => removeRow(idx)} className="text-stone-400 hover:text-red-600" data-testid={`grp-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-stone-100">
          <Badge tone="stone">Total: {visibleRows.length} lokasi</Badge>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Batal</Button>
            <Button onClick={saveAll} disabled={saving} data-testid="grp-save-btn">
              {saving ? "Menyimpan…" : "Simpan Semua"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default GroupedMaterialEditor;
